"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/analytics";

// Fires GA4 events for the YouTube OAuth return paths.
//
// connect_youtube_authorized — landed on /dashboard with ?youtube=connected
// connect_youtube_error      — /dashboard?youtube=error OR /invite-error?error=oauth_failed
// connect_youtube_declined   — /invite-error?error=access_denied (user clicked
//                              "Cancel" on Google's consent screen)
//
// connect_youtube_initiated is fired from the connect button itself
// (components/connect-self-youtube-button.tsx) since that's a client click.
//
// Dedupe: sessionStorage marker keyed on the event + raw search string, so
// reloads or back-navigation don't re-fire.

function fireOnce(eventName: string, params: Record<string, string>, key: string) {
  if (typeof window === "undefined") return;
  if (window.sessionStorage.getItem(key)) return;
  trackEvent(eventName, params);
  window.sessionStorage.setItem(key, String(Date.now()));
}

export function ConnectYoutubeTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const search = window.location.search;
    if (!search) return;
    const params = new URLSearchParams(search);

    if (pathname === "/dashboard") {
      const youtube = params.get("youtube");
      if (youtube === "connected") {
        fireOnce("connect_youtube_authorized", { method: "self" }, `cy_authorized_${search}`);
      } else if (youtube === "cancelled") {
        // User clicked Cancel on Google's consent screen (self-connect flow now
        // lands here instead of /invite-error).
        fireOnce(
          "connect_youtube_declined",
          { method: "self", reason: params.get("reason") || "access_denied" },
          `cy_declined_${search}`,
        );
      } else if (youtube === "error") {
        fireOnce(
          "connect_youtube_error",
          { method: "self", details: params.get("details") || "unknown" },
          `cy_error_${search}`,
        );
      }
      return;
    }

    if (pathname === "/invite-error") {
      // Invite-flow failures only (self-connect cancels now land on /dashboard).
      const error = params.get("error");
      if (error === "oauth_failed" || error === "missing_params") {
        fireOnce("connect_youtube_error", { method: "invite", details: error }, `cy_error_${search}`);
      }
    }
  }, [pathname]);

  return null;
}
